// Legacy DashboardComponent.vue parity: weekly per-type totals + month/year selectors.
//   - initial load / location change / year change → GET dashboard?month=&year=
//   - month change                                  → GET dashboard/month?month=&year=

import { useCallback, useEffect, useRef, useState } from 'react';

import { adminApi } from '../api/client';
import { ensureLocation, onLocationChanged } from '../session/location';
import type { DashboardTimeSeries } from '../types';

type ChartPayload = { week?: DashboardTimeSeries; chart?: { month?: DashboardTimeSeries; year?: DashboardTimeSeries } };

export const FIRST_DASHBOARD_YEAR = 2019;

export function dashboardYears(now = new Date()) {
  const years: number[] = [];
  for (let y = FIRST_DASHBOARD_YEAR; y <= now.getFullYear(); y++) years.push(y);
  return years;
}

export function usePeriodCharts() {
  const now = new Date();
  const [month, setMonthState] = useState(now.getMonth() + 1);
  const [year, setYearState] = useState(now.getFullYear());
  const [week, setWeek] = useState<DashboardTimeSeries | undefined>();
  const [monthChart, setMonthChart] = useState<DashboardTimeSeries | undefined>();
  const [yearChart, setYearChart] = useState<DashboardTimeSeries | undefined>();
  const [loading, setLoading] = useState(true);
  const period = useRef({ month, year });
  period.current = { month, year };

  const loadAll = useCallback(async (m: number, y: number) => {
    setLoading(true);
    try {
      await ensureLocation().catch(() => undefined);
      const res = await adminApi.get<ChartPayload>('dashboard', { params: { month: m, year: y } });
      setWeek(res.data?.week);
      setMonthChart(res.data?.chart?.month);
      setYearChart(res.data?.chart?.year);
    } catch {
      /* charts stay empty */
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMonth = useCallback(async (m: number, y: number) => {
    setLoading(true);
    try {
      const res = await adminApi.get<ChartPayload>('dashboard/month', { params: { month: m, year: y } });
      setMonthChart(res.data?.chart?.month);
    } catch {
      /* keep previous */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll(period.current.month, period.current.year);
    return onLocationChanged(() => void loadAll(period.current.month, period.current.year));
  }, [loadAll]);

  const setMonth = (m: number) => {
    setMonthState(m);
    void loadMonth(m, period.current.year);
  };

  const setYear = (y: number) => {
    setYearState(y);
    void loadAll(period.current.month, y);
  };

  return { month, year, setMonth, setYear, week, monthChart, yearChart, loading, refresh: () => loadAll(period.current.month, period.current.year) };
}
